// client/src/components/DevicesTab.jsx
// "מכשירים" tab on CampDetailPage. Lists devices with their 6-digit code,
// mode badge, "open terminal" CTA, edit, and archive.
//
// Cards visually distinguish EARN (mint strap, mint icon tile) from SPEND
// (sky strap, sky icon tile) so a manager scanning the wall knows at a
// glance which terminal is a teacher device and which is a cash desk.

import { useMemo, useState } from 'react';
import ListToolbar from './ListToolbar.jsx';
import { matchesText, sortRows } from '../lib/listTools.js';
import {
  useDevices,
  useCreateDevice,
  useUpdateDevice,
  useDeleteDevice,
  useMoveDevice,
} from '../hooks/useDevices.js';
import { useCamps } from '../hooks/useCamps.js';
import Spinner from './Spinner.jsx';
import Modal from './Modal.jsx';
import DeviceForm from './DeviceForm.jsx';
import { DevicePhoneMobileIcon } from './Icons.jsx';

export default function DevicesTab({ campId }) {
  const devices = useDevices(campId);
  const createMutation = useCreateDevice(campId);
  const updateMutation = useUpdateDevice(campId);
  const deleteMutation = useDeleteDevice(campId);

  const [creating, setCreating] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDirty, setEditDirty] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [moving, setMoving] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const all = devices.data ?? [];
  const shown = useMemo(
    () =>
      sortRows(
        all.filter((d) => matchesText(d, q, ['name', 'code'])),
        sort,
        {
          name: (d) => d.name,
          code: (d) => d.code,
          kind: (d) => (d.kind === 'KIOSK' ? 'קיוסק' : 'מסופון'),
          mode: (d) => d.mode,
          lastSeenAt: (d) => (d.lastSeenAt ? new Date(d.lastSeenAt) : null),
        }
      ),
    [all, q, sort]
  );

  if (devices.isLoading) return <Spinner label="טוען מכשירים..." />;
  if (devices.isError) {
    return (
      <div className="card-danger p-4 text-joy-900 text-sm">
        שגיאה: {devices.error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <RedeemLinkBanner />

      <ListToolbar
        search={{ value: q, onChange: setQ, placeholder: 'חיפוש לפי שם או קוד...' }}
        sort={{
          value: sort,
          onChange: setSort,
          options: [
            { key: 'name', label: 'שם' },
            { key: 'code', label: 'קוד' },
            { key: 'kind', label: 'סוג' },
            { key: 'mode', label: 'מצב' },
            { key: 'lastSeenAt', label: 'נראה לאחרונה' },
          ],
        }}
        count={{ shown: shown.length, total: all.length, noun: 'מכשירים' }}
      >
        <button type="button" className="btn-pop" onClick={() => setCreating(true)}>
          <PlusIcon className="w-4 h-4" />
          מכשיר חדש
        </button>
      </ListToolbar>

      {all.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} />
      ) : shown.length === 0 ? (
        <div className="card p-8 text-center text-sm text-ink-500">לא נמצאו מכשירים מתאימים לחיפוש.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((d) => (
            <DeviceCard
              key={d.id}
              device={d}
              campId={campId}
              onEdit={() => setEditing(d)}
              onRemove={() => setRemoving(d)}
              onMove={() => setMoving(d)}
            />
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        isDirty={createDirty}
        title="מכשיר חדש"
      >
        <DeviceForm
          campId={campId}
          submitLabel="צור מכשיר"
          onDirtyChange={setCreateDirty}
          onCancel={() => setCreating(false)}
          onSubmit={async (body) => {
            await createMutation.mutateAsync(body);
            setCreating(false);
          }}
        />
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        isDirty={editDirty}
        title="עריכת מכשיר"
      >
        {editing && (
          <DeviceForm
            initial={editing}
            campId={campId}
            submitLabel="שמור שינויים"
            onDirtyChange={setEditDirty}
            onCancel={() => setEditing(null)}
            onSubmit={async (patch) => {
              await updateMutation.mutateAsync({ deviceId: editing.id, patch });
              setEditing(null);
            }}
          />
        )}
      </Modal>

      {moving && (
        <MoveDeviceModal
          device={moving}
          campId={campId}
          onClose={() => setMoving(null)}
        />
      )}

      <Modal open={!!removing} onClose={() => setRemoving(null)} title="ארכוב מכשיר">
        {removing && (
          <>
            <p className="text-sm text-ink-700">
              לארכב את <strong>{removing.name}</strong>? המכשיר לא יוכל יותר
              להתחבר. ההיסטוריה נשמרת.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setRemoving(null)}>
                ביטול
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  await deleteMutation.mutateAsync(removing.id);
                  setRemoving(null);
                }}
              >
                {deleteMutation.isPending ? 'מארכב...' : 'כן, ארכב'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// Pointer to the public /redeem page — the browser channel for redeeming a
// printed points voucher AND for a balance/last-transactions inquiry, with no
// terminal needed. Lives at the top of the devices tab because it's the
// "device-less" alternative: a school without a terminal for every teacher can
// hand out vouchers and let students redeem here. URL uses the live origin so
// it shows tictac.matara.pro in prod and localhost in dev, and is copyable so
// a manager can paste it to families.
function RedeemLinkBanner() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/redeem`;
  const display = `${window.location.host}/redeem`;

  function copyLink() {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      },
      () => {/* clipboard blocked on insecure context — fail silent */},
    );
  }

  return (
    <div className="card-soft p-4 sm:p-5">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl grid place-items-center shrink-0 bg-brand-100 text-brand-700">
          <GlobeAltIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-ink-900">מימוש שוברים ובירור יתרה באתר</h3>
          <p className="text-sm text-ink-600 mt-0.5">
            תלמיד יכול לפדות שובר נקודות ולבדוק את יתרת הכרטיס שלו מכל דפדפן — בלי מסופון.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href="/redeem"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-sm"
            >
              <GlobeAltIcon className="w-4 h-4" />
              פתח את הדף
            </a>
            <code
              dir="ltr"
              className="font-mono text-xs text-brand-700 bg-white border border-ink-200 rounded-lg px-2.5 py-1.5"
            >
              {display}
            </code>
            <button type="button" onClick={copyLink} className="btn-ghost btn-sm" title="העתק קישור">
              {copied ? <CheckIcon className="w-4 h-4 text-mint-600" /> : <CopyIcon className="w-4 h-4" />}
              {copied ? 'הועתק' : 'העתק קישור'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Relative "last seen" in Hebrew. Coarse on purpose — the heartbeat is ~3 min.
function relTimeHe(ts) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 90) return 'הרגע';
  const m = Math.floor(s / 60);
  if (m < 60) return `לפני ${m} דק׳`;
  const h = Math.floor(m / 60);
  if (h < 24) return `לפני ${h} שע׳`;
  return `לפני ${Math.floor(h / 24)} ימים`;
}

function KioskStatus({ lastSeenAt }) {
  const online = lastSeenAt && Date.now() - new Date(lastSeenAt).getTime() < 7 * 60 * 1000;
  return (
    <div className="mt-3 flex items-center gap-2 text-xs">
      <span className={`w-2.5 h-2.5 rounded-full ${online ? 'bg-mint-500' : 'bg-ink-300'}`} />
      <span className={online ? 'text-mint-700 font-semibold' : 'text-ink-500'}>
        {online ? 'מחוברת כעת' : lastSeenAt ? `נראתה לאחרונה ${relTimeHe(lastSeenAt)}` : 'טרם התחברה'}
      </span>
    </div>
  );
}

function availabilityLabel(device) {
  if (device.availabilityMode === 'BLOCKED_ALWAYS') return 'חסומה כל היום';
  const ws = Array.isArray(device.availabilityWindows) ? device.availabilityWindows : [];
  const ranges = ws.map((w) => `${w.start}–${w.end}`).join(' · ');
  if (device.availabilityMode === 'ACTIVE_WINDOWS') return ranges ? `פעילה ${ranges}` : 'פעילה — ללא שעות';
  if (device.availabilityMode === 'BLOCKED_WINDOWS') return ranges ? `חסומה ${ranges}` : 'זמינה כל היום';
  return 'זמינה כל היום';
}

/**
 * "העברה לתוכנית אחרת" — moves the device with ALL its settings. The station
 * keeps working without re-login (the server reads its camp fresh on every
 * request); only a code collision in the target forces a new code, which the
 * modal surfaces prominently (a kiosk's fixed link embeds it).
 */
function MoveDeviceModal({ device, campId, onClose }) {
  const camps = useCamps();
  const move = useMoveDevice(campId);
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { device, codeChanged }

  const targets = (camps.data || []).filter((c) => c.id !== campId);

  async function submit() {
    setError(null);
    try {
      setResult(await move.mutateAsync({ deviceId: device.id, targetCampId: targetId }));
    } catch (err) {
      setError(
        err?.message === 'not_target_camp_manager' ? 'אין לך הרשאה על תוכנית היעד.' :
        /star/.test(err?.message || '') ? 'במחירון של המכשיר יש סכומים עם אגורות, ותוכנית היעד עובדת בנקודות שלמות בלבד. עדכנו את המחירון ונסו שוב.' :
        err?.status >= 500 ? `שגיאת שרת (${err.status}). נסו שוב בעוד רגע.` :
        'ההעברה נכשלה. נסו שוב.'
      );
    }
  }

  const targetName = targets.find((c) => c.id === targetId)?.name || '';

  return (
    <Modal open onClose={onClose} title="העברת מכשיר לתוכנית אחרת">
      {result ? (
        <div className="space-y-4">
          <div className="card p-4 bg-mint-50 border-mint-200 text-mint-900 text-sm">
            <strong className="block">{device.name} הועבר אל {targetName}.</strong>
            כל ההגדרות עברו יחד איתו, והוא ממשיך לעבוד בלי התחברות מחדש.
          </div>
          {result.codeChanged && (
            <div className="rounded-2xl bg-gold-50 border border-gold-200 text-gold-900 px-4 py-3 text-sm">
              <strong className="block">שימו לב — הוקצה קוד מכשיר חדש:</strong>
              <span className="font-mono num text-2xl font-bold tracking-[0.2em] block mt-1" dir="ltr">
                {result.device.code}
              </span>
              הקוד הקודם היה תפוס בתוכנית היעד. אם זו עמדה עם קישור קבוע
              (‎/kiosk?code=‎) — יש לעדכן את הקישור לקוד החדש.
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={onClose}>סגור</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-ink-700">
            העברת <strong>{device.name}</strong> לתוכנית אחרת, כולל כל ההגדרות —
            מצב, זמינות, שומר מסך, קוד מנהל ומחירון. ההיסטוריה של העסקאות נשארת
            בתוכנית הנוכחית, והמכשיר ממשיך לעבוד בלי התחברות מחדש.
          </p>
          {targets.length === 0 ? (
            <p className="rounded-2xl bg-ink-50 border border-ink-200 text-ink-600 px-4 py-3 text-sm">
              אין תוכנית נוספת שאתה מנהל. לפתיחת תוכנית חדשה יש לפנות למשרד.
            </p>
          ) : (
            <ul className="space-y-2 max-h-56 overflow-y-auto pe-1">
              {targets.map((c) => (
                <li key={c.id}>
                  <label
                    className={`flex items-center gap-3 rounded-2xl border px-4 py-2.5 cursor-pointer transition-colors
                      ${targetId === c.id ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-brand-300'}`}
                  >
                    <input
                      type="radio"
                      name="move-target"
                      className="accent-brand-600"
                      checked={targetId === c.id}
                      onChange={() => setTargetId(c.id)}
                    />
                    <span className="font-semibold text-ink-900">{c.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>ביטול</button>
            <button
              type="button"
              className="btn-primary"
              disabled={!targetId || move.isPending}
              onClick={submit}
            >
              {move.isPending ? 'מעביר...' : 'העבר מכשיר'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DeviceCard({ device, campId, onEdit, onRemove, onMove }) {
  const kiosk = device.kind === 'KIOSK';
  const earn = device.mode === 'EARN';
  const both = device.mode === 'BOTH';
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Self-auth kiosk URL — embeds the device code so a station on any network
  // opens straight into the kiosk with no manager login.
  const kioskLink = `${window.location.origin}/kiosk?code=${device.code}`;
  function copyLink() {
    navigator.clipboard.writeText(kioskLink).then(
      () => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 1600); },
      () => {},
    );
  }

  function copyCode() {
    navigator.clipboard.writeText(device.code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      },
      () => {/* clipboard blocked on insecure context — fine, fail silent */},
    );
  }

  return (
    <div className="card overflow-hidden flex flex-col hover:shadow-pop transition-shadow">
      {/* Strap colour reflects the mode. BOTH uses a tri-stop gradient
          (mint → brand → sky) so it reads as "this device can do both". */}
      <div
        className={`h-1.5 rounded-t-2xl ${
          kiosk
            ? 'bg-brand-gradient'
            : both
              ? 'bg-gradient-to-l from-mint-500 via-brand-500 to-sky-500'
              : earn
                ? 'bg-mint-gradient'
                : 'bg-sky-gradient'
        }`}
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          <div
            className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${
              kiosk
                ? 'bg-brand-100 text-brand-700'
                : both
                  ? 'bg-brand-100 text-brand-700'
                  : earn
                    ? 'bg-mint-100 text-mint-700'
                    : 'bg-sky-100 text-sky-700'
            }`}
          >
            {kiosk ? <MonitorIcon className="w-5 h-5" /> : <DevicePhoneMobileIcon className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-ink-900 truncate">{device.name}</h3>
            {kiosk ? (
              <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                <span className="badge-indigo">עמדת שירות עצמי</span>
                <span className="text-[11px] font-semibold text-ink-500 num">{availabilityLabel(device)}</span>
                {device.kioskManualEntry === false && (
                  <span className="text-[11px] font-semibold text-ink-500">ללא הקלדה ידנית</span>
                )}
              </div>
            ) : both ? (
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="badge-mint">מורה</span>
                <span className="badge-sky">חנות</span>
              </div>
            ) : (
              <span className={`mt-1 inline-block ${earn ? 'badge-mint' : 'badge-sky'}`}>
                {earn ? 'מורה · זיכוי' : 'חנות · חיוב'}
              </span>
            )}
          </div>
        </div>

        {/* The 6-digit pairing code is the device's identity. Big, monospaced,
            with a click-to-copy. */}
        <div className="mt-4 rounded-2xl bg-ink-50 border border-ink-200 p-3.5 flex items-center justify-between gap-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">קוד מכשיר</div>
            <div
              className="font-mono num text-2xl font-bold text-ink-900 tracking-[0.2em] mt-0.5"
              dir="ltr"
            >
              {device.code}
            </div>
          </div>
          <button
            type="button"
            onClick={copyCode}
            className="btn-ghost btn-sm"
            aria-label="העתק קוד"
            title="העתק"
          >
            {copied ? <CheckIcon className="w-4 h-4 text-mint-600" /> : <CopyIcon className="w-4 h-4" />}
            {copied ? 'הועתק' : 'העתק'}
          </button>
        </div>

        {/* Kiosk connection status — the station pings the server every few
            minutes; "connected" means a ping within the last 7 minutes. */}
        {kiosk && <KioskStatus lastSeenAt={device.lastSeenAt} />}

        {/* Shareable self-auth link for fixing the station to one URL. */}
        {kiosk && (
          <div className="mt-3 rounded-2xl bg-brand-50 border border-brand-100 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-brand-700">
              קישור לעמדה
            </div>
            <div className="mt-1 flex items-center gap-2">
              <code dir="ltr" className="flex-1 min-w-0 truncate font-mono text-xs text-ink-700">
                {kioskLink}
              </code>
              <button
                type="button"
                onClick={copyLink}
                className="btn-secondary btn-sm shrink-0"
                title="העתק קישור"
              >
                {linkCopied ? <CheckIcon className="w-4 h-4 text-mint-600" /> : <CopyIcon className="w-4 h-4" />}
                {linkCopied ? 'הועתק' : 'העתק'}
              </button>
            </div>
            <div className="mt-1.5 text-[11px] text-ink-500">פותח את העמדה בכל מכשיר/רשת, ללא צורך בכניסת מנהל.</div>
          </div>
        )}

        {/* Primary action — open the terminal pre-logged-in as this device.
            Server-side GET sets the device cookie and 302s to /terminal, so
            the new tab lands on the EARN/SPEND home with no code entry. */}
        <a
          href={`/api/camps/${campId}/devices/${device.id}/open-terminal`}
          target="_blank"
          rel="noopener noreferrer"
          className={`btn w-full justify-center mt-4 text-white shadow-sm hover:shadow ${
            kiosk
              ? 'bg-gradient-to-br from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800'
              : both
                ? 'bg-gradient-to-l from-mint-600 via-brand-600 to-sky-600 hover:brightness-110'
                : earn
                  ? 'bg-gradient-to-br from-mint-500 to-mint-700 hover:from-mint-600 hover:to-mint-800'
                  : 'bg-gradient-to-br from-sky-500  to-sky-700  hover:from-sky-600  to-sky-800'
          }`}
        >
          {kiosk ? <MonitorIcon className="w-4 h-4" /> : <DevicePhoneMobileIcon className="w-4 h-4" />}
          {kiosk ? 'פתח עמדה' : 'פתח מסופון'}
        </a>

        <div className="mt-3 pt-3 border-t border-ink-100 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button type="button" onClick={onEdit} className="btn-ghost btn-sm">
              ערוך
            </button>
            <button type="button" onClick={onMove} className="btn-ghost btn-sm">
              העבר
            </button>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="btn-sm text-joy-700 hover:bg-joy-50 rounded-lg px-3 py-1.5 font-semibold transition-colors"
          >
            ארכב
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-sky-gradient text-white grid place-items-center mb-4 shadow-pop">
        <DevicePhoneMobileIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">אין עדיין מכשירים</h2>
      <p className="mt-2 text-muted max-w-md mx-auto">
        צור מכשיר מורה לזיכוי נקודות, או מכשיר חנות לעמדת מכירה / קיוסק.
      </p>
      <button type="button" className="btn-pop btn-lg mt-5" onClick={onCreate}>
        <PlusIcon className="w-5 h-5" />
        מכשיר ראשון
      </button>
    </div>
  );
}

function PlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}
function MonitorIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}
function GlobeAltIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}
function CopyIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125H5.625c-.621 0-1.125-.504-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H7.5m1.5-3.75h3.375c.621 0 1.125.504 1.125 1.125v4.5h4.5a1.125 1.125 0 011.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125H10.5a1.125 1.125 0 01-1.125-1.125V4.875c0-.621.504-1.125 1.125-1.125z" />
    </svg>
  );
}
function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}
